import React from 'react';
import { Box, Container, Grid, Typography, Card, CardContent, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SecurityIcon from '@mui/icons-material/Security';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import SectionHeader from '../SectionHeader';

const ProblemSection = () => {
  const theme = useTheme();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.2,
        duration: 0.6,
      },
    }),
  };

  const problems = [
    {
      icon: <AccessTimeIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Потеря времени',
      description: 'Врачи тратят до 40% рабочего времени на общение с родственниками пациентов, снижая эффективность работы.',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Риски безопасности',
      description: 'Стандартные мессенджеры могут привести к раскрытию личных данных врачей и нарушению конфиденциальности.',
    },
    {
      icon: <SupervisedUserCircleIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Сложности коммуникации',
      description: 'Дежурные врачи часто не владеют полной информацией о пациентах, что затрудняет общение с родственниками.',
    },
  ];

  return (
    <Box
      id="problem"
      sx={{
        py: 10,
        backgroundColor: theme.palette.background.default,
      }}
      ref={ref}
    >
      <Container maxWidth="lg">
        <SectionHeader
          title="Проблема, которую мы решаем"
          subtitle="В современной медицинской практике существуют коммуникационные проблемы, снижающие эффективность работы врачей."
        />

        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 2 }}>
                В стационарах клиник пациентам и их родственникам необходимо общаться с врачами. Первая линия общения — дежурный врач, который не всегда компетентен в частных вопросах.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 2 }}>
                <strong>Лечащий врач тратит значительную часть рабочего времени на консультации</strong> с родственниками пациентов.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 2 }}>
                Клиники запрещают врачам общаться с пациентами в обычных мессенджерах под угрозой увольнения, что создает коммуникационный барьер.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                Для обеспечения непрерывности лечения необходимо сохранять историю общения с пациентами.
              </Typography>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {problems.map((problem, index) => (
                <Grid item xs={12} key={index}>
                  <motion.div
                    custom={index}
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={cardVariants}
                  >
                    <Card
                      elevation={2}
                      sx={{
                        height: '100%',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          {problem.icon}
                          <Typography variant="h5" sx={{ ml: 2, fontWeight: 500 }}>
                            {problem.title}
                          </Typography>
                        </Box>
                        <Typography variant="body1" color="text.secondary">
                          {problem.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProblemSection; 